const RECOVERABLE_LOAD_FRAGMENTS = [
  'chunkloaderror',
  'loading chunk',
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'networkerror',
  'failed to import module',
]

export const RECOVERY_RELOAD_KEY = 'gaia:runtime-reload-at'
export const RECOVERY_RELOAD_WINDOW_MS = 30_000

export function getRuntimeErrorMessage(errorLike) {
  if (typeof errorLike === 'string') {
    return errorLike
  }

  if (errorLike?.message) {
    return errorLike.message
  }

  if (errorLike?.reason?.message) {
    return errorLike.reason.message
  }

  return ''
}

export function isRecoverableAppLoadError(errorLike) {
  const message = getRuntimeErrorMessage(errorLike).toLowerCase()
  if (!message) {
    return false
  }

  return RECOVERABLE_LOAD_FRAGMENTS.some((fragment) => message.includes(fragment))
}

function buildRecoveryDetail(errorLike) {
  const message = getRuntimeErrorMessage(errorLike)

  if (message) {
    return `${message} Refresh to load the latest app assets and reconnect.`
  }

  return 'Unable to load the application. Refresh to load the latest app assets and reconnect.'
}

export function showFallbackScreen(root, title, detail) {
  root.innerHTML = `
    <div style="min-height: 100vh; background: #1a1a2e; display: flex; align-items: center; justify-content: center; font-family: 'Cinzel', 'Times New Roman', serif;">
      <div style="text-align: center; max-width: 480px; padding: 40px 24px;">
        <p style="font-size: 1.25rem; color: #d4a574; margin-bottom: 16px; text-shadow: 0 0 10px rgba(0,0,0,0.9);">${title}</p>
        <p style="color: #9ca3af; margin-bottom: 24px; font-family: system-ui, sans-serif; font-size: 0.875rem;">${detail}</p>
        <button onclick="window.location.reload()" style="padding: 12px 24px; background: #92400e; color: white; border: none; border-radius: 8px; cursor: pointer; font-family: 'Cinzel', 'Times New Roman', serif; font-size: 0.875rem;">
          Retry Connection
        </button>
      </div>
    </div>
  `
}

export function hasRecentRecoveryAttempt({
  storage = typeof window !== 'undefined' ? window.sessionStorage : undefined,
  now = Date.now(),
} = {}) {
  const lastAttemptAt = Number(storage?.getItem?.(RECOVERY_RELOAD_KEY) || 0)
  if (!Number.isFinite(lastAttemptAt) || lastAttemptAt <= 0) {
    return false
  }

  return (now - lastAttemptAt) <= RECOVERY_RELOAD_WINDOW_MS
}

function shouldReloadForRecovery(storage, now) {
  return !hasRecentRecoveryAttempt({ storage, now })
}

export function recoverFromAppLoadFailure(
  errorLike,
  {
    root,
    rootInstance,
    storage = window.sessionStorage,
    reload = () => window.location.reload(),
    now = Date.now(),
  } = {},
) {
  if (!isRecoverableAppLoadError(errorLike)) {
    return { handled: false, action: null }
  }

  if (shouldReloadForRecovery(storage, now)) {
    storage?.setItem?.(RECOVERY_RELOAD_KEY, String(now))
    reload()
    return { handled: true, action: 'reload' }
  }

  if (rootInstance?.unmount) {
    rootInstance.unmount()
  }

  if (root) {
    showFallbackScreen(root, 'Connection Lost', buildRecoveryDetail(errorLike))
  }

  return { handled: true, action: 'fallback' }
}
